import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import { Check, ChevronDown, CircleX, Search } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './multi-select.module.scss';

const PORTAL_GAP = 4;
const PORTAL_MAX_HEIGHT = 320;
const PORTAL_MIN_WIDTH = 220;
const PORTAL_EDGE_MARGIN = 8;
type PortalPosition = {
  top: number;
  left: number;
  right: number;
  width: number;
  placement: 'top' | 'bottom';
  // Anchor the dropdown's right edge to the trigger (open leftward) when a
  // left-aligned panel would overflow the viewport's right edge.
  alignRight: boolean;
};

const styles = pickCss(css, css.root);

export type MultiSelectOption = {
  label: string;
  value: string;
  color?: string;
  // Optional glyph rendered (muted, 16px) between the checkbox and the label
  // — e.g. the category icon in the products Category filter.
  icon?: ReactNode;
  // Optional facet count rendered as a right-aligned badge (e.g. "Brazil 20").
  // Omit for plain option lists; present for filter dropdowns driven by
  // `products.filterCounts`.
  count?: number;
  // Optional second-level options revealed (indented) under this option once it
  // is selected — e.g. subcategories under a content category. Their checked
  // state is a separate dimension driven by `childValues` / `onChildChange`.
  children?: MultiSelectOption[];
};

type MultiSelectProps = {
  'aria-label'?: string;
  className?: string;
  disabled?: boolean;
  disableLocalFilter?: boolean;
  emptyMessage?: string;
  id?: string;
  leadingIcon?: ReactNode;
  loading?: boolean;
  name?: string;
  onChange?: (values: string[]) => void;
  onSearchChange?: (query: string) => void;
  options: MultiSelectOption[];
  // Second selection dimension for nested `option.children` (e.g.
  // subcategories). Children render indented under a selected parent; toggling
  // one calls `onChildChange` with the next child-value set.
  childValues?: string[];
  onChildChange?: (values: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectAllLabel?: string;
  showSelectAll?: boolean;
  size?: 'sm' | 'default';
  style?: CSSProperties;
  values?: string[];
  // Render the dropdown in a body portal (position: fixed) so it floats above
  // and never extends the document in short/overflow-clipped containers.
  usePortal?: boolean;
};

const SELECT_ALL_VALUE = '__select_all__';

const CheckboxIndicator = ({ checked }: { checked: boolean }) => (
  <span className={joinCss(css.checkbox, checked && css.checkboxChecked)}>
    {checked && <Check className={css.checkIcon} />}
  </span>
);

export const MultiSelect = ({
  'aria-label': ariaLabel,
  className,
  disabled,
  disableLocalFilter = false,
  emptyMessage,
  leadingIcon,
  loading = false,
  onChange,
  onSearchChange,
  options,
  childValues = [],
  onChildChange,
  placeholder = 'Select…',
  searchable = true,
  searchPlaceholder = 'Search…',
  selectAllLabel = '(Select All)',
  showSelectAll = true,
  size = 'default',
  style,
  values = [],
  usePortal = false,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [portalPosition, setPortalPosition] = useState<PortalPosition | null>(
    null
  );

  const normalizedQuery =
    searchable && !disableLocalFilter ? query.trim().toLowerCase() : '';

  const visibleOptions = useMemo(() => {
    if (!normalizedQuery) return options;
    // Keep a parent whose own label matches, or that has a matching child so
    // searching can surface nested subcategories.
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(normalizedQuery) ||
        (o.children ?? []).some((c) =>
          c.label.toLowerCase().includes(normalizedQuery)
        )
    );
  }, [options, normalizedQuery]);

  const allSelected = options.length > 0 && values.length === options.length;
  const hasSelection = values.length > 0;

  const toggle = useCallback(
    (value: string) => {
      if (value === SELECT_ALL_VALUE) {
        onChange?.(allSelected ? [] : options.map((o) => o.value));
        return;
      }
      const next = values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value];
      onChange?.(next);
    },
    [values, allSelected, options, onChange]
  );

  const toggleChild = useCallback(
    (value: string) => {
      const next = childValues.includes(value)
        ? childValues.filter((v) => v !== value)
        : [...childValues, value];
      onChildChange?.(next);
    },
    [childValues, onChildChange]
  );

  const clearAll = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      onChange?.([]);
    },
    [onChange]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      onSearchChange?.('');
      return undefined;
    }
    if (!searchable) return undefined;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, searchable, onSearchChange]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideRoot = rootRef.current?.contains(target);
      const insidePortal = portalRef.current?.contains(target);
      if (!insideRoot && !insidePortal) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Portal placement: compute the trigger-relative viewport position and keep
  // it in sync on scroll / resize. Flips above the trigger when there's no
  // room below.
  useLayoutEffect(() => {
    if (!open || !usePortal) {
      setPortalPosition(null);
      return undefined;
    }
    const compute = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const placement: PortalPosition['placement'] =
        spaceBelow < PORTAL_MAX_HEIGHT + PORTAL_GAP && spaceAbove > spaceBelow
          ? 'top'
          : 'bottom';
      const estWidth = Math.max(rect.width, PORTAL_MIN_WIDTH);
      const alignRight =
        rect.left + estWidth > window.innerWidth - PORTAL_EDGE_MARGIN;
      setPortalPosition({
        top:
          placement === 'top'
            ? rect.top - PORTAL_GAP
            : rect.bottom + PORTAL_GAP,
        left: rect.left,
        right: window.innerWidth - rect.right,
        width: rect.width,
        placement,
        alignRight,
      });
    };
    compute();
    const onChange = () => compute();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [open, usePortal]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const dropdownContent = (
    <>
      {searchable && (
        <div className={css.searchRow}>
          <Search className={css.searchIcon} />
          <input
            className={css.searchInput}
            onChange={(e) => {
              const next = e.target.value;
              setQuery(next);
              onSearchChange?.(next);
            }}
            placeholder={searchPlaceholder}
            ref={searchInputRef}
            type="text"
            value={query}
          />
        </div>
      )}
      <div className={css.optionList}>
        {showSelectAll && options.length > 0 && (
          <button
            className={css.option}
            onClick={() => toggle(SELECT_ALL_VALUE)}
            type="button"
          >
            <CheckboxIndicator checked={allSelected} />
            <span className={css.optionLabel}>{selectAllLabel}</span>
          </button>
        )}
        {visibleOptions.map((option) => {
          const parentSelected = values.includes(option.value);
          const childList = option.children ?? [];
          // Reveal children once the parent is selected, or while searching if
          // a child matches the query (so subcategories are findable).
          const parentMatches =
            !normalizedQuery ||
            option.label.toLowerCase().includes(normalizedQuery);
          const childMatches =
            !!normalizedQuery &&
            childList.some((c) =>
              c.label.toLowerCase().includes(normalizedQuery)
            );
          const childrenToRender =
            childList.length === 0 || !(parentSelected || childMatches)
              ? []
              : normalizedQuery && !parentMatches
                ? childList.filter((c) =>
                    c.label.toLowerCase().includes(normalizedQuery)
                  )
                : childList;

          return (
            <div key={option.value}>
              <button
                className={css.option}
                onClick={() => toggle(option.value)}
                type="button"
              >
                <CheckboxIndicator checked={parentSelected} />
                {option.icon && (
                  <span aria-hidden className={css.optionIcon}>
                    {option.icon}
                  </span>
                )}
                {option.color && (
                  <span
                    aria-hidden
                    className={css.optionDot}
                    style={{ backgroundColor: option.color }}
                  />
                )}
                <span className={css.optionLabel}>{option.label}</span>
                {option.count !== undefined && (
                  <span className={css.optionCount}>{option.count}</span>
                )}
              </button>
              {childrenToRender.map((child) => (
                <button
                  className={joinCss(css.option, css.optionChild)}
                  key={child.value}
                  onClick={() => toggleChild(child.value)}
                  type="button"
                >
                  <CheckboxIndicator
                    checked={childValues.includes(child.value)}
                  />
                  {child.icon && (
                    <span aria-hidden className={css.optionIcon}>
                      {child.icon}
                    </span>
                  )}
                  <span className={css.optionLabel}>{child.label}</span>
                  {child.count !== undefined && (
                    <span className={css.optionCount}>{child.count}</span>
                  )}
                </button>
              ))}
            </div>
          );
        })}
        {loading && <span className={css.empty}>Loading…</span>}
        {!loading && visibleOptions.length === 0 && (
          <span className={css.empty}>{emptyMessage ?? 'No options'}</span>
        )}
      </div>
    </>
  );

  return (
    <div className={css.wrapper} ref={rootRef} style={style}>
      <button
        className={styles(
          { isDisabled: !!disabled, isOpen: open, size },
          className
        )}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        ref={triggerRef}
        aria-label={ariaLabel}
        type="button"
      >
        {hasSelection ? (
          // Selected state mirrors test.xthory.com: a single truncating "N
          // selected" label plus ONE clear (⊗), no chevron — so the trigger
          // never overflows its fixed filter width the way the old "+N more"
          // badge + separate ✕ + divider + chevron did.
          <>
            <span className={css.value}>{values.length} selected</span>
            <CircleX
              aria-label="Clear selection"
              className={css.clearAll}
              onClick={clearAll}
            />
          </>
        ) : (
          <>
            {leadingIcon && (
              <span className={css.leadingIcon}>{leadingIcon}</span>
            )}
            <span className={css.placeholder}>{placeholder}</span>
            <ChevronDown
              className={joinCss(css.chevron, open && css.chevronOpen)}
            />
          </>
        )}
      </button>

      {open && !usePortal && (
        <div className={css.dropdown}>{dropdownContent}</div>
      )}
      {open &&
        usePortal &&
        portalPosition &&
        createPortal(
          <div
            className={joinCss(css.dropdown, css.dropdownPortal)}
            ref={portalRef}
            style={{
              minWidth: portalPosition.width,
              position: 'fixed',
              ...(portalPosition.alignRight
                ? { right: portalPosition.right }
                : { left: portalPosition.left }),
              ...(portalPosition.placement === 'top'
                ? { bottom: window.innerHeight - portalPosition.top }
                : { top: portalPosition.top }),
            }}
          >
            {dropdownContent}
          </div>,
          document.body
        )}
    </div>
  );
};
